import { useState } from 'react';
import { Box, Avatar, Typography, Tooltip, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PositionOrEmpty, Reward, PositionModalData, Position } from './types';
import { RewardsModal } from './RewardsModal';

// ─── Reward badge config ────────────────────────────────────────────────────

interface BadgeDef {
    key: keyof Reward;
    color: string;
    label: string;
}

const getActiveBadges = (reward: Reward) => {
    const badges: { key: string; color: string; label: string }[] = [];
    if (reward.main_award)
        badges.push({ key: 'main_award', color: '#3BA27A', label: 'Выплата' });
    if (reward.referal_1 || reward.referal_2 || reward.referal_3)
        badges.push({ key: 'referal', color: '#818DD6', label: 'Рефер.' });
    if (reward.clone_reward)
        badges.push({ key: 'clone_reward', color: '#C975A7', label: 'ПКлон' });
    if (reward.upgrade)
        badges.push({ key: 'upgrade', color: '#00ABCB', label: 'Апгрейд' });
    return badges;
};

const hasSpecial = (reward: Reward) => !!(reward.special && reward.special.length > 0);

// ─── Component ──────────────────────────────────────────────────────────────

interface Props {
    position: PositionOrEmpty;
    isMentor: boolean;
    reward?: Reward;
    slotIndex?: number;
    currency: string;
}

export const PositionCell = ({ position, isMentor, reward, slotIndex, currency }: Props) => {
    const [open, setOpen] = useState(false);

    const pos = position && typeof position !== 'boolean' ? position : null;
    const avatarSrc = pos?.avatar ? (pos.avatar as string) : undefined;
    const login = pos?.login || (isMentor ? '?' : 'Пусто');
    const isEmpty = !pos;
    const activeBadges = reward ? getActiveBadges(reward) : [];

    const isClickable = isMentor ? !!pos : !!reward;

    const getModalData = (): PositionModalData | null => {
        if (isMentor && pos) return { kind: 'mentor', position: pos as Position };
        if (!isMentor && reward) return { kind: 'slot', reward, position, slotIndex: slotIndex ?? 1 };
        return null;
    };

    return (
        <>
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    pt: 2.5,   // space for the info button at top
                    pb: 0.75,
                    borderRadius: 2,
                    cursor: isClickable ? 'pointer' : 'default',
                    transition: 'background 0.15s',
                    '&:hover': isClickable ? { bgcolor: 'action.hover' } : {},
                }}
                onClick={() => isClickable && setOpen(true)}
            >
                {/* ── Info button — always visible ── */}
                {isClickable && (
                    <Tooltip title="Нажмите для подробной информации" placement="top" arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                            sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                p: 0.25,
                                color: 'primary.main',
                                bgcolor: 'background.paper',
                                boxShadow: 1,
                                '&:hover': { bgcolor: 'background.paper', opacity: 0.85 },
                            }}
                        >
                            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                )}

                {/* ── Avatar ── */}
                <Avatar
                    src={avatarSrc}
                    sx={{
                        width: isMentor ? 40 : 32,
                        height: isMentor ? 40 : 32,
                        bgcolor: isEmpty ? 'grey.700' : isMentor ? 'primary.main' : 'secondary.main',
                        border: isMentor ? '2px solid' : undefined,
                        borderColor: isMentor ? 'primary.main' : undefined,
                        fontSize: isMentor ? '1.5rem' : '1.1rem',
                    }}
                >
                    {!avatarSrc && (isEmpty ? '?' : login[0]?.toUpperCase())}
                </Avatar>

                {/* ── Login ── */}
                <Typography
                    variant="caption"
                    sx={{
                        color: isEmpty ? 'text.main' : 'text.main',
                        textAlign: 'center',
                        fontSize: isMentor ? '0.9rem' : '0.9rem',
                        fontWeight: isMentor ? 600 : 500,
                        maxWidth: 90,
                        wordBreak: 'break-word',
                        lineHeight: 1.3,
                    }}
                >
                    {login}
                </Typography>

                {/* ── Куратор под ментором ── */}
                {isMentor && pos?.kurator && (
                    <Typography variant="caption" color="text.main" sx={{ fontSize: '0.85rem' }}>
                        ({pos.kurator})
                    </Typography>
                )}

                {/* ── Reward badges (child slots only) ── */}
                {!isMentor && activeBadges.length > 0 && (
                    <Box display="flex" flexWrap="wrap" justifyContent="center" gap={0.4} maxWidth={90} mt={0.25}>
                        {activeBadges.map((b) => (
                            <Tooltip key={b.key} title={b.label} placement="top" arrow>
                                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: b.color, flexShrink: 0 }} />
                            </Tooltip>
                        ))}
                    </Box>
                )}
            </Box>

            <RewardsModal
                open={open}
                onClose={() => setOpen(false)}
                data={getModalData()}
                currency={currency}
            />
        </>
    );
};